import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Upload,
  Button,
  Steps,
  Row,
  Col,
  Table,
  Form,
  Input,
  InputNumber,
  Space,
  message,
  Spin,
  Tag,
  Alert,
  Divider,
  Image,
  Tooltip,
  Progress
} from 'antd';
import {
  CameraOutlined,
  CheckCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { ocrApi, quotationApi } from '@/services/api';

const { Dragger } = Upload;
const { Step } = Steps;

const QuotationOCR = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [editedData, setEditedData] = useState(null);

  // 步驟1: 上傳截圖
  const handleUpload = async (file) => {
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      message.loading({ content: '正在辨識截圖...', key: 'ocr', duration: 0 });
      
      const response = await ocrApi.processImage(formData);
      
      message.destroy('ocr');
      
      if (response.success) {
        setUploadedImage(URL.createObjectURL(file));
        setOcrResult(response.data);
        setEditedData(response.data); // 初始化編輯資料
        
        message.success('辨識完成！');
        setCurrentStep(1);
      } else {
        message.error('辨識失敗：' + response.error);
      }
    } catch (error) {
      message.error('上傳失敗：' + error.message);
    } finally {
      setLoading(false);
    }
    
    return false; // 阻止自動上傳
  };

  const uploadProps = {
    name: 'image',
    accept: 'image/*',
    maxCount: 1,
    beforeUpload: handleUpload,
    showUploadList: false,
  };

  // 信心度標籤顏色
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.7) return 'warning';
    return 'error';
  };

  const getConfidenceText = (confidence) => {
    if (confidence >= 0.9) return '高信心度';
    if (confidence >= 0.7) return '中信心度';
    return '低信心度';
  };

  // 步驟2: 檢視與修正
  const renderReviewStep = () => {
    if (!ocrResult) return null;

    const { customer, items } = ocrResult;

    // 品項表格欄位
    const columns = [
      {
        title: '行號',
        dataIndex: 'lineNumber',
        width: 60,
      },
      {
        title: '品名',
        dataIndex: 'productName',
        width: 200,
        render: (text, record) => (
          <Space direction="vertical" size="small">
            <span>{record.matchedProduct?.productName || text}</span>
            <Tag color={getConfidenceColor(record.confidence)}>
              {getConfidenceText(record.confidence)}
            </Tag>
          </Space>
        ),
      },
      {
        title: 'SKU',
        dataIndex: ['matchedProduct', 'sku'],
        width: 120,
        render: (sku, record) => (
          sku ? (
            <Tag color="blue">{sku}</Tag>
          ) : (
            <Tag color="red">需手動選擇</Tag>
          )
        ),
      },
      {
        title: '數量',
        dataIndex: 'quantity',
        width: 100,
      },
      {
        title: '信心度',
        dataIndex: 'confidence',
        width: 100,
        render: (confidence) => (
          <Progress
            percent={Math.round(confidence * 100)}
            size="small"
            status={confidence >= 0.7 ? 'success' : 'exception'}
          />
        ),
      },
      {
        title: '操作',
        width: 100,
        render: (_, record) => (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditItem(record)}
          >
            修正
          </Button>
        ),
      },
    ];

    return (
      <Row gutter={24}>
        {/* 左側：原始截圖 */}
        <Col span={10}>
          <Card title="原始截圖" size="small">
            <Image
              src={uploadedImage}
              alt="Uploaded Screenshot"
              style={{ width: '100%', maxHeight: 500, objectFit: 'contain' }}
            />
          </Card>
        </Col>

        {/* 右側：辨識結果 */}
        <Col span={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 客戶資訊 */}
            <Card
              title={
                <Space>
                  客戶資訊
                  {customer.matched && (
                    <Tag color="green">已匹配既有客戶</Tag>
                  )}
                  <Tag color={getConfidenceColor(customer.confidence)}>
                    {getConfidenceText(customer.confidence)}
                  </Tag>
                </Space>
              }
              size="small"
              extra={
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {/* TODO: 開啟編輯對話框 */}}
                >
                  修正
                </Button>
              }
            >
              <Row gutter={16}>
                <Col span={12}>
                  <div><strong>姓名：</strong>{customer.name || '未辨識'}</div>
                </Col>
                <Col span={12}>
                  <div><strong>電話：</strong>{customer.phone || '未辨識'}</div>
                </Col>
                <Col span={24} style={{ marginTop: 8 }}>
                  <div><strong>地址：</strong>{customer.address || '未辨識'}</div>
                </Col>
                {customer.email && (
                  <Col span={24} style={{ marginTop: 8 }}>
                    <div><strong>Email：</strong>{customer.email}</div>
                  </Col>
                )}
              </Row>
            </Card>

            {/* 品項清單 */}
            <Card
              title={
                <Space>
                  品項清單
                  <Tag>{items?.totalItems || 0} 項</Tag>
                  {items?.needsReview && (
                    <Tag color="warning" icon={<WarningOutlined />}>
                      部分品項需要確認
                    </Tag>
                  )}
                </Space>
              }
              size="small"
            >
              {items?.needsReview && (
                <Alert
                  message="提示"
                  description="部分品項辨識信心度較低或無法自動對碼，請檢查並手動修正。"
                  type="warning"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                />
              )}
              
              <Table
                columns={columns}
                dataSource={items?.items || []}
                pagination={false}
                size="small"
                rowKey="lineNumber"
                scroll={{ y: 300 }}
              />
            </Card>

            {/* 操作按鈕 */}
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setCurrentStep(0)}>
                重新上傳
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleConfirmAndCreate}
                loading={loading}
              >
                確認並建立報價單
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>
    );
  };

  // 步驟3: 建立報價單
  const handleConfirmAndCreate = async () => {
    setLoading(true);
    
    try {
      // 準備報價單資料
      const quotationData = {
        contactName: editedData.customer.name,
        contactPhone: editedData.customer.phone,
        contactEmail: editedData.customer.email,
        shippingAddress: editedData.customer.address,
        customerId: editedData.customer.customerId || null,
        items: editedData.items.items
          .filter(item => item.matchedProduct) // 只保留已對碼的品項
          .map(item => ({
            productId: item.matchedProduct.productId,
            sku: item.matchedProduct.sku,
            quantity: item.quantity,
            ocrConfidence: item.confidence,
            manualVerified: false,
            notes: item.rawText
          })),
        notes: `由 OCR 自動建立 (辨識信心度: ${Math.round(ocrResult.metadata.overallConfidence * 100)}%)`
      };
      
      const response = await quotationApi.createQuotation(quotationData);
      
      if (response.success) {
        message.success('報價單建立成功！');
        setCurrentStep(2);
        
        // 3秒後跳轉
        setTimeout(() => {
          navigate(`/quotations/${response.data.id}`);
        }, 3000);
      } else {
        message.error('建立失敗：' + response.error);
      }
    } catch (error) {
      message.error('建立失敗：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 編輯品項
  const handleEditItem = (item) => {
    // TODO: 開啟編輯對話框
    message.info('編輯功能開發中...');
  };

  // 步驟3: 完成
  const renderCompleteStep = () => (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
      <h2 style={{ marginTop: 24 }}>報價單建立成功！</h2>
      <p>正在跳轉到報價單詳情頁面...</p>
    </div>
  );

  return (
    <div>
      <Card
        title={
          <Space>
            <CameraOutlined />
            OCR 快速建單
          </Space>
        }
        extra={
          <Button onClick={() => navigate('/quotations')}>
            返回列表
          </Button>
        }
      >
        {/* 步驟條 */}
        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          <Step title="上傳截圖" icon={<CameraOutlined />} />
          <Step title="檢視與修正" icon={<EditOutlined />} />
          <Step title="建立完成" icon={<CheckCircleOutlined />} />
        </Steps>

        <Divider />

        {/* 步驟內容 */}
        <Spin spinning={loading} tip="處理中...">
          {currentStep === 0 && (
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
              <Alert
                message="使用說明"
                description={
                  <ul style={{ marginBottom: 0 }}>
                    <li>支援上傳客戶訂單截圖（JPG/PNG 格式）</li>
                    <li>系統將自動辨識客戶姓名、電話、地址與品項</li>
                    <li>辨識完成後可手動修正錯誤項目</li>
                    <li>確認無誤後即可快速建立報價單</li>
                  </ul>
                }
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Dragger {...uploadProps} disabled={loading}>
                <p className="ant-upload-drag-icon">
                  <CameraOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">點擊或拖曳上傳截圖</p>
                <p className="ant-upload-hint">
                  支援 JPG、PNG 格式，建議檔案大小 &lt; 10MB
                </p>
              </Dragger>
            </div>
          )}

          {currentStep === 1 && renderReviewStep()}
          {currentStep === 2 && renderCompleteStep()}
        </Spin>
      </Card>
    </div>
  );
};

export default QuotationOCR;
